import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, ChevronDown, ChevronUp, Download, FileText, Home, ChevronRight, Search, Loader2, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import ProcessFlowDiagram from '@/components/ProcessFlowDiagram';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Engineering = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedPart, setExpandedPart] = useState(null);

  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/parts`);
      setParts(response.data);
    } catch (error) {
      console.error('Error fetching parts:', error);
      toast.error('Failed to load parts');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (partNumber) => {
    if (!window.confirm(`Delete part ${partNumber}?`)) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/parts/${partNumber}`);
      toast.success('Part deleted successfully');
      fetchParts();
    } catch (error) {
      console.error('Error deleting part:', error);
      toast.error('Failed to delete part');
    }
  };

  const handleDownloadDrawing = async (partNumber, drawing) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/parts/${partNumber}/drawings/${drawing.filepath}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', drawing.filename || drawing.filepath);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded: ${drawing.filename}`);
    } catch (error) {
      console.error('Error downloading drawing:', error);
      toast.error('Failed to download drawing');
    }
  };

  const filteredParts = parts.filter(part => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      part.part_number.toLowerCase().includes(query) ||
      part.drawing_number.toLowerCase().includes(query) ||
      part.description.toLowerCase().includes(query) ||
      part.material_grade.toLowerCase().includes(query) ||
      part.customer?.toLowerCase().includes(query)
    );
  });

  const togglePartExpansion = (partNumber) => {
    setExpandedPart(expandedPart === partNumber ? null : partNumber);
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-eng" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(59, 130, 246, 0.15)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(59, 130, 246, 0.15)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-eng)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-blue-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-blue-400 hover:bg-blue-500/10" data-testid="back-to-home-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                <Wrench className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide" data-testid="engineering-title">Engineering</h1>
                <p className="text-xs text-slate-500">Part Master Database</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-blue-400 font-medium">Engineering</span>
          </nav>
          <div className="flex items-center gap-3">
            <Button onClick={() => navigate('/engineering/create')} className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50 hover:shadow-[0_0_20px_rgba(59,130,246,0.3)]" data-testid="create-part-btn">
              <Plus className="w-4 h-4 mr-2" /> Create New Part
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1600px] mx-auto space-y-6">

        {/* Search Bar */}
        <div className="bg-slate-900/40 backdrop-blur-sm border border-blue-500/10 rounded-lg px-4 py-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by part number, drawing, description..."
                className="pl-10 bg-slate-950/50 border-slate-800 text-white placeholder:text-slate-600 focus:border-blue-500/50"
                data-testid="search-parts-input"
              />
            </div>
            {searchQuery && (
              <p className="text-sm text-slate-400">
                Found <span className="text-blue-400 font-bold">{filteredParts.length}</span> of {parts.length} parts
              </p>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" data-testid="loading-state" />
          </div>
        ) : filteredParts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="empty-state">
            <div className="w-20 h-20 rounded-full bg-slate-900/60 border border-slate-800 flex items-center justify-center mb-6">
              <Wrench className="w-10 h-10 text-slate-600" />
            </div>
            {searchQuery ? (
              <>
                <h3 className="text-lg font-bold text-slate-400 mb-2">No parts match your search</h3>
                <Button onClick={() => setSearchQuery('')} variant="outline" className="border-slate-700">Clear Search</Button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold text-slate-400 mb-2">No parts created yet</h3>
                <Button onClick={() => navigate('/engineering/create')} className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50">Create Your First Part</Button>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4" data-testid="parts-list">
            {filteredParts.map((part) => {
              const isExpanded = expandedPart === part.part_number;
              
              return (
                <div
                  key={part.part_number}
                  className="bg-slate-900/40 backdrop-blur-md border border-blue-500/10 rounded-xl hover:border-blue-400/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(59,130,246,0.1)]"
                  data-testid={`part-card-${part.part_number}`}
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Part Number</p>
                            <p className="font-mono text-white font-bold" data-testid="part-number">{part.part_number}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Drawing</p>
                            <p className="font-mono text-slate-300">{part.drawing_number}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Material</p>
                            <p className="text-slate-300">{part.material_grade}</p>
                          </div>
                          <div>
                            <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Diameter</p>
                            <p className="text-slate-300">{part.diameter} mm</p>
                          </div>
                        </div>
                        <div className="mb-4">
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Description</p>
                          <p className="text-slate-300 text-sm" data-testid="part-description">{part.description}</p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        {/* Download Drawing Button - show if drawings exist */}
                        {part.drawings && part.drawings.length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadDrawing(part.part_number, part.drawings[0]);
                            }}
                            data-testid={`download-drawing-btn-${part.part_number}`}
                            className="border-blue-600 text-blue-400 hover:bg-blue-950/50"
                            title={`Download: ${part.drawings[0].filename}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => togglePartExpansion(part.part_number)}
                          data-testid="view-workflow-btn"
                          className="border-border hover:border-primary"
                        >
                          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/engineering/edit/${part.part_number}`)}
                          data-testid="edit-part-btn"
                          className="border-border hover:border-primary"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(part.part_number)}
                          data-testid="delete-part-btn"
                          className="border-border hover:border-destructive text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {!isExpanded && (
                      <div className="pt-4 border-t border-border">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Process Sequence</p>
                        <div className="flex flex-wrap gap-2">
                          {part.process_sequence.map((proc, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-muted text-white text-xs font-mono rounded-sm border border-border"
                              data-testid="process-badge"
                            >
                              {idx + 1}. {proc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Expanded Process Flow Diagram */}
                  {isExpanded && (
                    <div className="border-t border-border bg-muted/20 p-6">
                      <h4 className="text-white font-bold uppercase tracking-wide mb-4 text-sm">
                        Manufacturing Process Flow
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1">
                          <ProcessFlowDiagram
                            processSequence={part.process_sequence}
                            processStatuses={{}}
                            className="max-w-sm"
                          />
                        </div>
                        <div className="md:col-span-2 space-y-4">
                          {/* Process Details */}
                          <div className="bg-card border border-border rounded-sm p-6">
                            <h5 className="text-white font-bold uppercase tracking-wider text-xs mb-4">
                              Process Details
                            </h5>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Material</p>
                                <p className="text-white">{part.material_grade} - Ø{part.diameter}mm</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Heat Treatment</p>
                                <p className="text-white">{part.heat_treatment}</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Length of Cut</p>
                                <p className="text-white">{part.loc} mm</p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Pieces per Bar</p>
                                <p className="text-white font-bold text-xl text-primary">
                                  {Math.floor(5800 / (part.loc + 2))} pieces
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Process Steps</p>
                                <p className="text-white font-bold text-xl">{part.process_sequence.length}</p>
                              </div>
                            </div>
                          </div>

                          {/* Drawings Section */}
                          <div className="bg-card border border-blue-800 rounded-sm p-6">
                            <h5 className="text-blue-400 font-bold uppercase tracking-wider text-xs mb-4 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Drawings & Documents
                            </h5>
                            {part.drawings && part.drawings.length > 0 ? (
                              <div className="space-y-2">
                                {part.drawings.map((drawing, idx) => (
                                  <div 
                                    key={idx}
                                    className="flex items-center justify-between bg-muted/30 rounded-sm px-4 py-3 border border-border"
                                  >
                                    <div className="flex items-center gap-3">
                                      <FileText className="w-5 h-5 text-blue-400" />
                                      <div>
                                        <p className="text-white font-medium text-sm">{drawing.filename}</p>
                                        <p className="text-xs text-muted-foreground">
                                          Uploaded: {new Date(drawing.uploaded_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDownloadDrawing(part.part_number, drawing)}
                                      data-testid={`download-drawing-${idx}`}
                                      className="border-blue-600 text-blue-400 hover:bg-blue-950/50"
                                    >
                                      <Download className="w-4 h-4 mr-2" />
                                      Download
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-center py-4">
                                <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                <p className="text-muted-foreground text-sm">No drawings uploaded</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Edit this part to upload drawings
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
      </main>
    </div>
  );
};

export default Engineering;